import TextField from '@material-ui/core/TextField';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { ScaffolderField } from '@backstage/plugin-scaffolder-react/alpha';
import type {
  CustomFieldValidator,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';

type UniqueAppNameUiOptions = Record<string, never>;

/** Plain text input for the app name - the collision check itself lives in validateUniqueAppName below. */
export const UniqueAppNamePickerField = (
  props: FieldExtensionComponentProps<string, UniqueAppNameUiOptions>,
) => {
  const { onChange, rawErrors, required, formData, schema } = props;

  return (
    <ScaffolderField
      rawDescription={schema.description}
      rawErrors={rawErrors}
      required={required}
    >
      <TextField
        label={schema.title ?? 'Name'}
        required={required}
        value={formData ?? ''}
        error={(rawErrors?.length ?? 0) > 0}
        onChange={e => onChange(e.target.value)}
        fullWidth
      />
    </ScaffolderField>
  );
};

/**
 * Catches an app name already registered in the same tier's catalog
 * lifecycle, before the user ever submits the form. Best-effort only - the
 * avantus-k8s generator script's own uniqueness check remains the real
 * source of truth (see plan-name-collision-check.md for the known race
 * window around in-flight retirements).
 */
export const validateUniqueAppName: CustomFieldValidator<
  string,
  UniqueAppNameUiOptions
> = async (value, validation, context) => {
  const catalogApi = context.apiHolder.get(catalogApiRef);
  if (!catalogApi || !value) return;

  const tier = (context.formData?.tier as string | undefined) ?? 'experimental';
  const { items } = await catalogApi.getEntities({
    filter: {
      kind: 'Component',
      'metadata.name': value,
      'spec.lifecycle': tier,
    },
    fields: ['metadata.name'],
  });

  if (items.length > 0) {
    validation.addError(
      `An app named "${value}" already exists in the ${tier} tier. Choose a different name or retire the existing one first.`,
    );
  }
};
